import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../utils/axios";
import { PencilIcon, XMarkIcon } from "@heroicons/react/20/solid";
import useDialogStore from "../stores/useDialogStore";
import { toast } from "react-toastify";

const DEVICE_OPTIONS = [
  {
    key: "finisher",
    label: "Finisher",
    exclusive: [],
    color: "white",
  },
  {
    key: "rt_lct",
    label: "RT / LCT",
    exclusive: [],
    color: "white",
  },
  {
    key: "no_df",
    label: "No DF",
    exclusive: [],
    color: "white",
  },
  {
    key: "blue",
    label: "Blue",
    exclusive: ["green"],
    color: "blue",
  },
  {
    key: "green",
    label: "Green",
    exclusive: ["blue"],
    color: "green",
  },
  {
    key: "cabinet",
    label: "Cabinet",
    exclusive: [],
    color: "white",
  },
];

function OptionButton({ label, checked, onClick, color }) {
  let selectedBg = "bg-gray-200";
  let selectedBorder = "border-gray-600";
  let selectedDot = "bg-gray-600";
  if (color === "green") {
    selectedBg = "bg-green-200";
    selectedBorder = "border-green-600";
    selectedDot = "bg-green-600";
  } else if (color === "blue") {
    selectedBg = "bg-blue-200";
    selectedBorder = "border-blue-600";
    selectedDot = "bg-blue-600";
  }
  return (
    <div
      className={`flex items-center px-4 border rounded h-8 mr-2 mb-2 ${
        checked ? selectedBg : "bg-white"
      } ${checked ? selectedBorder : "border-textBlue"}`}
      onClick={onClick}
      style={{ cursor: "pointer" }}
    >
      <div
        className={`w-4 h-4 border rounded-full mr-2 ${
          checked ? selectedDot : "bg-white border-textBlue"
        }`}
      />
      {label}
    </div>
  );
}

const EditableOptionsCell = ({ deviceId, deviceOptions = {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();
  const { openDialog, closeDialog } = useDialogStore();
  const [options, setOptions] = useState({});
  const [trays, setTrays] = useState(4);

  useEffect(() => {
    if (deviceOptions) {
      const initialOptions = {};
      DEVICE_OPTIONS.forEach((opt) => {
        initialOptions[opt.key] = deviceOptions[opt.key] === 1;
      });
      setOptions(initialOptions);
      setTrays(deviceOptions.trays || 4);
    }
  }, [deviceOptions]);

  const mutation = useMutation({
    mutationFn: async (data) => {
      return await axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/device-options/patch/${deviceId}`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["device-options", deviceId]);
      toast.success("Options saved successfully");
      setIsEditing(false);
    },
    onError: () => {
      toast.error("Failed to save options");
    },
  });

  const handleOptionClick = (key) => {
    setOptions((prev) => {
      const newValue = !prev[key];
      let updated = { ...prev, [key]: newValue };
      if (newValue) {
        // Turn off exclusives
        const exclusive =
          DEVICE_OPTIONS.find((o) => o.key === key)?.exclusive || [];
        exclusive.forEach((exKey) => {
          updated[exKey] = false;
        });
      }
      return updated;
    });
  };

  const onSave = () => {
    const payload = {};
    DEVICE_OPTIONS.forEach((opt) => {
      payload[opt.key] = options[opt.key] ? 1 : 0;
    });
    payload.trays = trays;

    openDialog({
      title: "Confirm Changes",
      content: (
        <div>
          <p>Are you sure you want to save these changes?</p>
          <div className="flex justify-end gap-2 mt-4">
            <button
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              onClick={() => closeDialog()}
            >
              Cancel
            </button>
            <button
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              onClick={() => {
                mutation.mutate(payload);
                closeDialog();
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      ),
    });
  };

  const handleCancel = () => {
    // We'll consider any change to options or trays as dirty
    const dirty =
      JSON.stringify(options) !==
        JSON.stringify(
          Object.fromEntries(
            DEVICE_OPTIONS.map((opt) => [opt.key, deviceOptions[opt.key] === 1])
          )
        ) || trays !== (deviceOptions.trays || 4);
    if (dirty) {
      openDialog({
        title: "Discard Changes",
        content: (
          <div>
            <p>Are you sure you want to discard your changes?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                onClick={() => closeDialog()}
              >
                Keep Editing
              </button>
              <button
                className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200"
                onClick={() => {
                  // Reset to original
                  const initialOptions = {};
                  DEVICE_OPTIONS.forEach((opt) => {
                    initialOptions[opt.key] = deviceOptions[opt.key] === 1;
                  });
                  setOptions(initialOptions);
                  setTrays(deviceOptions.trays || 4);
                  setIsEditing(false);
                  closeDialog();
                }}
              >
                Discard Changes
              </button>
            </div>
          </div>
        ),
      });
    } else {
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const hasOptions = deviceOptions && Object.keys(deviceOptions).length > 0;
  const selectedOptions = hasOptions
    ? DEVICE_OPTIONS.filter((opt) => deviceOptions[opt.key] === 1)
    : [];

  return (
    <td
      onDoubleClick={() => setIsEditing(true)}
      className="whitespace-nowrap  text-sm text-black relative group"
    >
      {isEditing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
          onKeyDown={handleKeyDown}
          className="space-y-3 bg-gray-50 p-2 max-w-[400px] rounded-lg border border-gray-200"
        >
          <div className="flex flex-wrap">
            {DEVICE_OPTIONS.map((opt) => (
              <OptionButton
                key={opt.key}
                label={opt.label}
                checked={options[opt.key]}
                color={opt.color}
                onClick={() => handleOptionClick(opt.key)}
              />
            ))}
          </div>
          <div className="flex flex-wrap mb-4">
            <div className="flex items-center px-4 border border-textBlue rounded h-8 mr-2 bg-lightBlue">
              <label className="w-full py-4 text-sm font-medium text-gray mr-2">
                Trays
              </label>
              <div className="flex items-center space-x-4">
                {[1, 2, 3, 4].map((option) => (
                  <div key={option} className="flex items-center">
                    <input
                      type="radio"
                      id={`trays-${option}`}
                      value={option}
                      checked={trays === option}
                      onChange={() => setTrays(option)}
                      className="w-4 h-4 text-blue-600   bg-white border-textBlue"
                    />
                    <label
                      htmlFor={`trays-${option}`}
                      className="ml-2 text-sm font-medium text-gray"
                    >
                      {option}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white rounded-md hover:bg-gray-50 border border-gray-300 transition-colors duration-150"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors duration-150"
            >
              Save Changes
            </button>
          </div>
        </form>
      ) : (
        <div className="relative rounded-lg">
          {!hasOptions ? (
            <div className="text-gray-400 text-sm">No options available</div>
          ) : selectedOptions.length === 0 ? (
            deviceOptions.trays ? (
              <div className="text-gray-700 text-sm">
                Trays: {deviceOptions.trays}
              </div>
            ) : (
              <div className="text-gray-400 text-sm">No options selected</div>
            )
          ) : (
            <div
              className={`inline-flex items-center gap-1.5 p-1 rounded-md px-2 overflow-x-auto whitespace-nowrap ${
                selectedOptions.some((opt) => opt.key === "green")
                  ? "bg-green-100"
                  : selectedOptions.some((opt) => opt.key === "blue")
                  ? "bg-blue-100"
                  : ""
              }`}
            >
              {selectedOptions.map((opt, index, array) => (
                <React.Fragment key={opt.key}>
                  <span className={`text-sm text-gray-700 capitalize`}>
                    {opt.label}
                  </span>
                  {index < array.length - 1 && (
                    <span className="text-gray-300 mx-1">|</span>
                  )}
                </React.Fragment>
              ))}
              {deviceOptions.trays && (
                <>
                  {selectedOptions.length > 0 && (
                    <span className="text-gray-300 mx-1">|</span>
                  )}
                  <span className="text-sm text-gray-700">
                    Trays: {deviceOptions.trays}
                  </span>
                </>
              )}
            </div>
          )}
          <div className="absolute top-1 -right-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="flex items-center gap-1.5 text-gray-500 bg-white px-2 py-1 rounded-md border border-gray-200">
              <PencilIcon className="h-3.5 w-3.5" />
              <span className="text-xs">Double click to edit</span>
            </div>
          </div>
        </div>
      )}
    </td>
  );
};

export default EditableOptionsCell;
